import { format, isSameDay, eachDayOfInterval, startOfWeek, addMonths, subMonths } from "date-fns";
import { ScheduleTrans, DaysOfTheWeekTrans, getTranslatedMonthFromDate } from "../../utils/translations";
import { useState } from "react";

const getBookingBreakdown = (classObj) => {
  const bookings = classObj?.bookings || [];
  const confirmed = bookings.filter((b) => b.status === "confirmed");
  const online = confirmed.filter((b) => b.online === true).length;
  const studio = confirmed.filter((b) => !b.online).length;
  const waitlist = (classObj?.waitingList || []).length;
  return { online, studio, waitlist };
};

const getDayBookingBreakdown = (classObjs) => {
  return (classObjs || []).reduce(
    (acc, c) => {
      const { online, studio, waitlist } = getBookingBreakdown(c);
      acc.online += online;
      acc.studio += studio;
      acc.waitlist += waitlist;
      return acc;
    },
    { online: 0, studio: 0, waitlist: 0 }
  );
};

export default function DesktopCalendar({
  classes,
  language,
  today,
  getBookingForClass,
  handleBookClick,
  getClassesForDate,
  translateMonth,
  translateLocationName,
  isAdmin,
}) {
  const details = ScheduleTrans[language];
  const daysTrans = DaysOfTheWeekTrans[language];

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const weekStart = startOfWeek(currentMonth);
  const calendarStart = weekStart;
  const calendarEnd = new Date(weekStart);
  calendarEnd.setDate(calendarEnd.getDate() + 28);

  const getCalendarDays = () => {
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const startDate = startOfWeek(firstDay);
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    return eachDayOfInterval({ start: startDate, end: endDate });
  };

  const calendarDays = getCalendarDays();
  const calendarWeeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    calendarWeeks.push(calendarDays.slice(i, i + 7));
  }

  const formatClassTime = (dt) =>
    new Date(dt).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Jerusalem",
    });

  const sortByStart = (arr) =>
    [...(arr || [])].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  const getAgendaPreviewItems = (classesForDay, maxItems = 2) => {
    const sorted = sortByStart(classesForDay);
    const visible = sorted.slice(0, maxItems);
    const hiddenCount = Math.max(0, sorted.length - visible.length);
    return { sorted, visible, hiddenCount };
  };

  return (
    <div className="hidden md:block w-11/12 mx-auto mb-3">
      <h2 className="text-2xl font-bold mb-6 mx-auto mb-16">{details.upcomingClasses}</h2>
      <div className="flex gap-4 items-start">
        <div className="bg-beige rounded-2xl p-4 shadow overflow-x-auto flex-1 min-w-0">
          {/* Month navigation */}
          <div className="flex justify-between items-center mb-4">
            <button
              className="text-brown font-bold px-3 py-1 border-1 border-gray-300 rounded hover:bg-beige transition"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              type="button"
            >
              ◀
            </button>
            <h3 className="text-lg font-bold text-brown">
              {getTranslatedMonthFromDate(currentMonth, daysTrans)} {format(currentMonth, "yyyy")}
            </h3>
            <button
              className="text-brown font-bold px-3 py-1 border-1 border-gray-300 rounded hover:bg-beige transition"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              type="button"
            >
              ▶
            </button>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {["sun", "mon", "tue", "wed", "thu", "fri", "sat"].map((d) => (
              <div key={d} className="text-center font-bold text-brown mb-2 text-lg">
                {daysTrans[d]}
              </div>
            ))}
            {calendarWeeks.map((week, wIdx) =>
              week.map((date, idx) => {
                const classesForDay = getClassesForDate(date) || [];
                const hasClass = classesForDay.length > 0;
                const { sorted, visible, hiddenCount } = getAgendaPreviewItems(classesForDay, 2);
                const firstClass = hasClass ? sorted[0] : null;
                const booking = firstClass ? getBookingForClass(firstClass._id) : null;
                const isToday = isSameDay(date, today);

                const now = new Date();
                const allPast = hasClass && sorted.every((c) => new Date(c.start) < now);

                if (!hasClass) {
                  return (
                    <div
                      key={`${wIdx}-${idx}`}
                      className="relative flex flex-col items-center justify-center rounded-xl border min-w-[80px] min-h-[112px] p-2 bg-beige border-beige text-gray-600"
                    >
                      <div className="font-semibold text-lg">{format(date, "d")}</div>
                      {isToday && (
                        <span className="absolute bottom-1 w-1 h-1 bg-brown rounded-full" aria-hidden="true" />
                      )}
                    </div>
                  );
                }

                const isOnlineBooking = booking?.online === true;
                const isIndividual = firstClass?.isIndividual === true;
                const individualHasBooking = isIndividual && (firstClass?.bookedCount > 0 || booking);
                const cellStyles = isIndividual
                  ? (individualHasBooking ? "bg-purple-100 border-purple-500" : "bg-white border-purple-500 shadow-md")
                  : (booking
                    ? (isOnlineBooking ? "bg-blue-100 border-blue-500" : "bg-green-100 border-green-500")
                    : "bg-white border-brown shadow-md");

                return (
                  <div
                    key={`${wIdx}-${idx}`}
                    className={`
                      flex flex-col items-center justify-start rounded-xl border
                      min-w-[80px] min-h-[112px] p-2 relative
                      ${cellStyles}
                      ${allPast ? "opacity-40 pointer-events-none cursor-default" : "cursor-pointer hover:bg-brown hover:text-white transition"}
                    `}
                    style={!allPast ? { cursor: "pointer" } : undefined}
                    onClick={() => {
                      if (allPast) return;
                      if (sorted.length > 1) {
                        setSelectedDate(date);
                        setIsDetailsOpen(true);
                        return;
                      }
                      handleBookClick(firstClass);
                    }}
                  >
                    <div className="relative z-10 shrink-0 font-semibold text-lg">{format(date, "d")}</div>

                    {/* Vertically centered in the cell; block is centered horizontally, lines left-aligned to the same edge */}
                    <div className="pointer-events-none absolute inset-x-0 top-[53%] z-[1] flex -translate-y-1/2 justify-center px-2">
                      <div className="w-fit max-w-full text-left">
                        <div className="flex flex-col gap-0.5">
                          {visible.map((c) => (
                            <div key={c._id} className="max-w-full truncate text-[11px] leading-tight">
                              <span className="font-semibold">{formatClassTime(c.start)}</span>{" "}
                              <span className="opacity-90">{c.title || c.description}</span>
                            </div>
                          ))}
                          {hiddenCount > 0 && (
                            <div className="text-[11px] opacity-80">+{hiddenCount} more</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="relative z-10 mt-auto flex min-h-[3rem] shrink-0 flex-col items-center justify-end">
                      {(() => {
                        const { online, studio, waitlist } = getDayBookingBreakdown(sorted);
                        if (online === 0 && studio === 0 && waitlist === 0) return null;
                        return (
                          <div className="mt-1 text-xs flex items-center justify-center gap-1.5 flex-wrap">
                            {online > 0 && <span className="text-blue-600" title="Online">🖥️ {online}</span>}
                            {studio > 0 && <span className="text-green-700" title="In studio">🏠 {studio}</span>}
                            {waitlist > 0 && <span className="text-amber-700" title="Waitlist">⏳ {waitlist}</span>}
                          </div>
                        );
                      })()}

                      {!isAdmin && booking && (
                        <span className={`mt-1 text-xs font-bold ${booking.online ? "text-blue-600" : "text-darkBrown"}`}>
                          {booking.online ? details.bookLabelOnline : (firstClass.locationName || details.bookLabelStudio)}
                        </span>
                      )}
                    </div>

                    {isToday && (
                      <span className="absolute bottom-1 w-1 h-1 bg-brown rounded-full" aria-hidden="true" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {isDetailsOpen && (
          <>
            {/* Backdrop: click outside sidebar to close */}
            <button
              type="button"
              className="hidden lg:block fixed inset-0 z-40 cursor-default"
              aria-label="Close day details"
              onClick={() => setIsDetailsOpen(false)}
            />

            <aside className="hidden lg:block w-[320px] shrink-0 sticky top-4 z-50">
              <div className="bg-white rounded-2xl shadow border border-brown/10 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-baseline gap-3 text-left">
                    <div className="shrink-0 text-lg font-bold leading-none text-brown">
                      {selectedDate ? format(selectedDate, "d") : ""}{" "}
                      {selectedDate ? translateMonth(format(selectedDate, "MMM")) : ""}
                    </div>
                    <div className="truncate text-sm leading-none text-gray-500">
                      {details.upcomingClasses}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-700 transition"
                    onClick={() => setIsDetailsOpen(false)}
                    aria-label="Close day details"
                  >
                    ✕
                  </button>
                </div>

                <div className="mt-3 flex flex-col gap-2">
                  {(selectedDate ? sortByStart(getClassesForDate(selectedDate) || []) : []).map((c) => (
                    <button
                      key={c._id}
                      type="button"
                      className="text-left rounded-xl border border-gray-100 hover:border-brown/30 hover:bg-beige/40 transition px-3 py-2"
                      onClick={() => handleBookClick(c)}
                    >
                      <div className="text-sm font-semibold text-brown">
                        {formatClassTime(c.start)} · {c.title || c.description}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {c.durationMinutes} {details.min}
                        {c.locationName ? ` · ${translateLocationName(c.locationName)}` : ""}
                      </div>
                    {(() => {
                      const { online, studio, waitlist } = getBookingBreakdown(c);
                      if (online === 0 && studio === 0 && waitlist === 0) return null;
                      return (
                        <div className="mt-1 text-xs flex items-center gap-2 flex-wrap">
                          {online > 0 && <span className="text-blue-600" title="Online">🖥️ {online}</span>}
                          {studio > 0 && <span className="text-green-700" title="In studio">🏠 {studio}</span>}
                          {waitlist > 0 && <span className="text-amber-700" title="Waitlist">⏳ {waitlist}</span>}
                        </div>
                      );
                    })()}
                    </button>
                  ))}
                </div>
              </div>
            </aside>
          </>
        )}
      </div>
    </div>
  );
}